import type { VehicleSituation } from "../handlers";

/** Back-end: ExpenseType enum (1-7) */
export const EXPENSE_TYPE_CODES = {
	Infraction: 1,
	Maintenance: 2,
	Toll: 3,
	Reimbursement: 4,
	Fuel: 5,
	Trip: 6,
	Other: 7,
} as const;

export type ExpenseTypeCode =
	(typeof EXPENSE_TYPE_CODES)[keyof typeof EXPENSE_TYPE_CODES];

/** Back-end: MaintenanceType enum */
export const MAINTENANCE_TYPE_CODES = {
	Preventive: 1,
	Corrective: 2,
} as const;

export type MaintenanceTypeCode =
	(typeof MAINTENANCE_TYPE_CODES)[keyof typeof MAINTENANCE_TYPE_CODES];

/** Back-end: ExpenseRankingFilter / ExpenseRankingStatus (0-4) */
export type ExpenseRankingStatus = 0 | 1 | 2 | 3 | 4;

/** Label por código de tipo de despesa (para exibição em cards/relatórios) */
export const EXPENSE_TYPE_CODE_LABELS: Record<ExpenseTypeCode, string> = {
	1: "Infração",
	2: "Manutenção",
	3: "Pedágio",
	4: "Reembolso",
	5: "Combustível",
	6: "Viagem",
	7: "Outros",
};
export type ExpensesPaginatedResponse = {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
};

/** Mapeia label da UI para código do back-end */
export const EXPENSE_TYPE_LABEL_TO_CODE: Record<string, ExpenseTypeCode> = {
	Infração: 1,
	Manutenção: 2,
	Pedágio: 3,
	Reembolso: 4,
	Combustível: 5,
	Viagem: 6,
	Outros: 7,
} as const;

export const EXPENSE_TYPE_OPTIONS = [
	"Manutenção",
	"Infração",
	"Pedágio",
	"Combustível",
	"Reembolso",
	"Viagem",
	"Outros",
] as const;

export type ExpenseType = keyof typeof EXPENSE_TYPE_CODE_LABELS;

export type ExpenseTotalByType = {
	type: ExpenseType;
	total: number;
};

export type VehicleRankingItem = {
	vehicleId: number;
	plate: string;
	brand: string;
	model: string;
	modelYear: number;
	vehicleStatus: VehicleSituation;
	status: ExpenseRankingStatus;
	totalCurrentMonth: number;
	totalPreviousMonth: number;
	percentVsFleetAverage: number;
	percentVsPreviousMonth: number;
	totalsByType: ExpenseTotalByType[];
};

export type ExpensesRankingResponseApi = {
	header: ExpensesRankingHeader;
	vehicles: ExpensesByVehicleResponse;
};

/** Status usado no filtro da UI (0 = Todos, 1-4 = status específicos) */
export type ExpenseStatus = 0 | 1 | 2 | 3 | 4;

export type ExpenseCategoryItem = {
	nome: string;
	valor: number;
};

export type ExpensesRankingHeader = {
	fleetTotalCurrentMonth: number;
	averagePerVehicleCurrentMonth: number;
	outOfPatternCount: number;
	attentionCount: number;
	totalVehicles: number;
	vehiclesWithExpensesCurrentMonth: number;
	year: number;
	month: number;
};

export type ExpensesByVehicleFilters = {
	status?: ExpenseStatus;
	page?: number;
	pageSize?: number;
};

export type ExpensesByVehicleResponse = ExpensesPaginatedResponse & {
	items: VehicleRankingItem[];
};

/** Payload da API POST /api/expenses */
export type CreateExpensePayloadApi = {
	vehicleId: number;
	type: ExpenseTypeCode;
	amount: number;
	date: string;
	description?: string;
	fileName?: string;
	fileKey?: string;
	infractionNumber?: string;
	infractionDueDate?: string;
	driverIdentificationDueDate?: string;
	maintenanceType?: MaintenanceTypeCode;
	reimbursementReason?: number;
	fuelLiters?: number;
};

/** Payload da API PUT /api/expenses/{expenseId} */
export type UpdateExpensePayloadApi = {
	amount: number;
	date: string;
	description?: string;
	fileName?: string;
	fileKey?: string;
	infractionNumber?: string;
	infractionDueDate?: string;
	driverIdentificationDueDate?: string;
	maintenanceType?: MaintenanceTypeCode;
	reimbursementReason?: number;
	fuelLiters?: number;
};

export type VehicleOption = {
	id: string | number;
	placa: string;
	label: string;
};

export type ExpenseConsolidatedByPlateFilters = {
	startDate: string;
	endDate: string;
	PageNumber: number;
	PageSize: number;
	VehicleIds?: string[];
};

export type ExpenseConsolidatedByPlateItem = {
	vehicleId: number;
	plate: string;
	brand: string;
	model: string;
	total: number;
	infraction: number;
	maintenance: number;
	toll: number;
	reimbursement: number;
	fuel: number;
	trip: number;
	other: number;
};

export type ExpenseConsolidatedByPlateFilterResponse = {
	startDate: string;
	endDate: string;
	days: number;
	selectedVehiclesCount: number;
};

export type ExpenseConsolidatedByPlatePaginatedResponse = {
	filter: ExpenseConsolidatedByPlateFilterResponse;
	items: ExpensesPaginatedResponse & {
		items: ExpenseConsolidatedByPlateItem[];
	};
};
